import { ExclamationCircleIcon, PencilSquareIcon } from '@heroicons/react/24/outline'
import { Form, Formik } from 'formik'
import { get } from 'lodash'
import styled from 'styled-components'
import { v4 } from 'uuid'

import AdditionalPermissionsModal from '@/components/AdditionalPermissionsModal'
import { getOrganization } from '@/components/App/selectors'
import ShareWithUsers from '@/components/GenericForm/ShareOwnership'
import { ImageLibraryWithFormik } from '@/components/ImageLibrary'
import Label from '@/components/Label'
import Modal from '@/components/Modal'
import { Role, useRoles } from '@/components/RolesContainer'
import Button from '@/components/TailwindUI/Button'
import LoadingSpinner from '@/components/TailwindUI/LoadingSpinner'
import TextArea from '@/components/TailwindUI/TextArea'
import TextInput from '@/components/TailwindUI/TextInput'
import useFeatures from '@/components/useFeatures'
import { WORKSHOP_AGENTS } from '@/constants/featureToggles'
import { useAppSelector } from '@/store/hooks'
import { getCurrentSpace, getCurrentUser } from '@/store/selectors'

import { partialAppSchema } from './appSchema'

const StyledImageLibraryWithFormik = styled(ImageLibraryWithFormik)`
  && {
    .label {
      font-weight: normal;
    }
  }
`

export interface AppSubmission {
  name: string
  description: string
  instructions?: string
  iconUrl?: string
  audience?: object[]
  template: any
  sharedWith: string[]
}

interface CreateAppModalProps {
  isEditing?: boolean
  isLoading?: boolean
  isError?: boolean
  isOpen?: boolean
  readOnly?: boolean
  formValues?: Partial<AppSubmission>
  onSubmit: (values: Partial<AppSubmission>) => void
  onClose: () => void
}

const CreateAppModal = ({
  isOpen = true,
  isEditing = false,
  isLoading = false,
  isError = false,
  readOnly = false,
  onSubmit,
  onClose,
  formValues = {}
}: CreateAppModalProps) => {
  const roles = useRoles()
  const organization = useAppSelector(getOrganization)
  const currentSpace = useAppSelector(getCurrentSpace)
  const featureFlags = useFeatures()
  const agentsFlagOn = !!featureFlags[WORKSHOP_AGENTS]

  return roles.isAllowedAccess([Role.WORKSHOP_APP_OWNER], organization?.id, currentSpace?.id) ? (
    <Modal isOpen={isOpen} onClose={onClose} size="md" className="max-w-3xl">
      <div className="mx-8 my-3">
        <div className="flex items-center">
          <h3 className="mb-6 font-bold">
            {isEditing ? `Edit ${agentsFlagOn ? 'Agent' : 'App'}` : `New ${agentsFlagOn ? 'Agent' : 'App'}`}
          </h3>
        </div>
        <div className="row">
          <PencilSquareIcon className="mr-2 h-6 w-6 text-blue-800" />
          <h3 className="font-bold">Details</h3>
        </div>
        <CreateAppForm
          formValues={formValues}
          onSubmit={onSubmit}
          isLoading={isLoading}
          isEditing={isEditing}
          isError={isError}
          onCancel={onClose}
          readOnly={readOnly}
        />{' '}
      </div>
    </Modal>
  ) : (
    <AdditionalPermissionsModal isOpen={isOpen} onClose={onClose} requiredRoles={[Role.WORKSHOP_APP_OWNER]} />
  )
}

export default CreateAppModal

interface CreateAppFormProps {
  isStep?: boolean
  isEditing?: boolean
  isLoading?: boolean
  isError?: boolean
  readOnly?: boolean
  formValues?: Partial<AppSubmission>
  onSubmit: (values: Partial<AppSubmission>) => void
  onCancel: () => void
}

export function CreateAppForm({
  formValues,
  onSubmit,
  onCancel,
  isEditing = false,
  isStep = false,
  isLoading = false,
  isError = false,
  readOnly = false
}: CreateAppFormProps) {
  const currentUser = useAppSelector(getCurrentUser)
  const featureFlags = useFeatures()
  const agentsFlagOn = !!featureFlags[WORKSHOP_AGENTS]

  const currentUserId = currentUser?.id || ''
  const initialValues = {
    name: '',
    description: '',
    iconUrl: '',
    sharedWith: [currentUserId],
    template: ''
  } as AppSubmission

  // default audience to current user
  initialValues.audience = [
    {
      id: v4(),
      filters: [
        {
          attribute: {
            displayName: 'Individual',
            id: 'individual',
            type: 'string',
            scimAttributeName: 'individual',
            urn: 'id'
          },
          displayValue: currentUser.displayName || currentUser.email,
          id: v4(),
          ruleTextDisplayValue: currentUser.displayName || currentUser.email,
          operator: 'eq',
          value: currentUser.id,
          scimExpression: {
            type: 'filter',
            value: `id eq "${currentUser.id}"`
          }
        }
      ],
      scimExpression: {
        type: 'filter',
        value: `id eq "${currentUser.id}"`
      }
    }
  ]

  if (agentsFlagOn) {
    const description = get(formValues, 'description', '')
    try {
      const agentDescription = JSON.parse(description)?.agentDescription
      const instructions = JSON.parse(description)?.agentInstructions
      formValues = {
        ...formValues,
        description: agentDescription,
        instructions
      }
    } catch {}
  }

  const initialAppValues = { ...initialValues, ...formValues }

  return (
    <Formik
      onSubmit={values => onSubmit(values)}
      validationSchema={() => partialAppSchema(agentsFlagOn)}
      initialValues={initialAppValues}
    >
      {formik => (
        <Form className="w-full">
          <div className="h-full max-h-screen pr-4 pl-2 pb-2">
            <div className="border-b border-gray-900/10 pb-8">
              <div className="mt-5">
                <Label required>{agentsFlagOn ? 'Agent' : 'App'} name</Label>
                <TextInput
                  name="name"
                  value={get(formik.values, `name`)}
                  required={true}
                  placeholder={`Give your ${agentsFlagOn ? 'agent' : 'app'} a name`}
                  onChange={e => formik.setFieldValue('name', e.target.value)}
                  error={formik.touched.name && formik.errors.name ? formik.errors.name?.toString() : ''}
                  disabled={readOnly}
                />
              </div>
              <div className="mt-5">
                <Label required className="mb-2">
                  {agentsFlagOn ? 'Agent' : 'App'} icon
                </Label>
                <StyledImageLibraryWithFormik
                  formik
                  name="iconUrl"
                  className="icon-preview-image"
                  placeholder={'https://www.exampleurl.com/logo.png'}
                  required={true}
                  readOnly={readOnly}
                />
              </div>
              <div className="mt-5 mb-5">
                <Label required className="mb-2">
                  {agentsFlagOn ? 'Agent' : 'App'} description
                </Label>
                <TextArea
                  name="description"
                  value={get(formik.values, `description`)}
                  required={true}
                  onChange={e => formik.setFieldValue('description', e.target.value)}
                  error={
                    formik.touched?.description && formik.errors?.description
                      ? formik.errors?.description?.toString()
                      : undefined
                  }
                  placeholder={`Describe what your ${agentsFlagOn ? 'agent' : 'app'} does`}
                  disabled={readOnly}
                />
              </div>
              {agentsFlagOn && (
                <div className="mt-5 mb-5">
                  <Label className="mb-2">Instructions</Label>
                  <TextArea
                    name="instructions"
                    value={get(formik.values, `instructions`)}
                    onChange={e => formik.setFieldValue('instructions', e.target.value)}
                    placeholder={'Give instructions to your agent on how to use its tools'}
                    disabled={readOnly}
                  />
                </div>
              )}
              <ShareWithUsers
                formatProps={{ name: 'sharedWith', label: agentsFlagOn ? 'Owners' : 'App Owners' }}
                descriptor={agentsFlagOn ? 'agent' : 'app'}
                value={get(formik.values, `sharedWith`) as string[]}
                onSelect={users => {
                  formik.setFieldValue('sharedWith', users)
                }}
                disabled={readOnly}
              />
            </div>
            <div className="w-full border-t border-gray-300" />
          </div>
          <div className="mt-2 flex items-center justify-end gap-x-2 pr-4">
            {!readOnly && (
              <>
                {!isStep && (
                  <Button color="secondary" onClick={onCancel}>
                    Cancel
                  </Button>
                )}
                <Button color="primary" disabled={isLoading}>
                  {isLoading ? (
                    <div className="mx-2 flex w-8 items-center justify-center">
                      <LoadingSpinner className="h-6 w-6" />
                    </div>
                  ) : (
                    <div className="mx-2 flex w-8 items-center justify-center">
                      {isStep ? 'Next' : isEditing ? 'Update' : 'Create'}
                    </div>
                  )}
                </Button>
              </>
            )}
          </div>
          {isError && (
            <div className="mt-3 flex justify-end text-red-700">
              <ExclamationCircleIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
              <div className="ml-1">{`Failed to ${isEditing ? 'update' : 'create'} the ${
                agentsFlagOn ? 'agent' : 'app'
              }`}</div>
            </div>
          )}
        </Form>
      )}
    </Formik>
  )
}
